import { useCallback, useMemo, type ReactNode } from "react";
import { Text, Flex, Icon, Spinner } from "@chakra-ui/react";

import { Badge, Button } from "@digital-services-gard-as/anchor-react";
import { CellContext, ColumnDef } from "@tanstack/react-table";
import { ChevronDownIcon, ChevronUpIcon } from "@digital-services-gard-as/anchor-icons";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FiPlus } from "react-icons/fi";
import { datadogRum } from "@datadog/browser-rum";
import { ClaimsItemDto } from "@/api/claimsApi";
import { Tooltip } from "@/components/ui/tooltip";
import { TruncatedText } from "@/components/TruncatedText";
import { useTranslation } from "react-i18next";
import { InvoiceAddressModal } from "./InvoiceAddressModal";
import {
    createEmptyInvoiceMutationOptions,
    deleteInvoiceMutationOptions,
    updateInvoiceByPutMutationOptions
} from "@/api/queryOptions/invoiceAPIQueryOptions";
import { toaster } from "@/components/ui/toaster";
import { useSlowRequestToast } from "@/hooks/useSlowRequestToast";

const ADDRESS_CELL_MIN_SIZE = 70;
const ADDRESS_CELL_MAX_SIZE = 75;
const INVOICE_COUNT_CELL_MIN_SIZE = 60;
const INVOICE_COUNT_CELL_MAX_SIZE = 85;
const STATUS_HEADER_MAX_SIZE = 65;
const TOAST_DURATION = 5000;

const tableText =
    (tableId: string) =>
    ({ row }: CellContext<ClaimsItemDto, unknown>) => <TruncatedText text={row.getValue(tableId)} />;

const statusBadge = (status: string) => {
    switch (status) {
        case "Open":
            return <Badge variant="warning">{status}</Badge>;
        case "Closed":
            return <Badge variant="neutral">{status}</Badge>;
        case "VAT Missing":
            return <Badge variant="danger">{status}</Badge>;
        default:
            return <Badge variant="neutral">{status}</Badge>;
    }
};

export function useColumns() {
    const { t } = useTranslation("claimsInvoicesOverview");

    return useMemo<ColumnDef<ClaimsItemDto>[]>(
        () => [
            {
                header: t("claimsTable.incidentDateHeader") as string | undefined,
                accessorKey: "incidentDate",
                cell: ({ row }) => {
                    return (
                        <Flex gap="0.5rem">
                            {row.getIsExpanded() ? (
                                <Icon
                                    alignSelf="center"
                                    boxSize={3}
                                    id={"closeExpanderBtn" + row.id}
                                    aria-label="expand"
                                    asChild
                                >
                                    <ChevronUpIcon />
                                </Icon>
                            ) : (
                                <Icon
                                    alignSelf="center"
                                    boxSize={3}
                                    id={"openExpanderBtn" + row.id}
                                    aria-label="close"
                                    asChild
                                >
                                    <ChevronDownIcon />
                                </Icon>
                            )}
                            <Text>{row.getValue("incidentDate")}</Text>
                        </Flex>
                    );
                },
                enableSorting: true,
                sortDescFirst: true
            },
            {
                header: t("claimsTable.claimsIdHeader"),
                id: "id",
                accessorKey: "id",
                cell: tableText("id"),
                enableSorting: true,
                sortDescFirst: true
            },
            {
                header: t("claimsTable.descriptionHeader"),
                accessorKey: "description",
                cell: tableText("description"),
                enableSorting: false
            },
            {
                header: t("claimsTable.vesselHeader"),
                accessorKey: "vesselName",
                cell: tableText("vesselName"),
                enableSorting: true,
                sortDescFirst: true
            },
            {
                header: t("claimsTable.address"),
                accessorKey: "address",
                cell: ({ row }) => <AddressCell claimId={row.original.id} />,
                enableSorting: false,
                minSize: ADDRESS_CELL_MIN_SIZE,
                maxSize: ADDRESS_CELL_MAX_SIZE
            },
            {
                header: t("claimsTable.clientHeader"),
                id: "clientName",
                accessorKey: "clientName",
                enableSorting: false
            },
            {
                header: t("claimsTable.invoicesHeader"),
                id: "invoiceCount",
                accessorKey: "invoiceCount",
                cell: ({ row }) => (
                    <CreateInvoiceCell
                        rowId={row.id}
                        claimId={row.original.id}
                        invoiceCount={row.getValue("invoiceCount")}
                    />
                ),
                minSize: INVOICE_COUNT_CELL_MIN_SIZE,
                maxSize: INVOICE_COUNT_CELL_MAX_SIZE,
                enableSorting: false
            },
            {
                header: t("claimsTable.statusHeader"),
                id: "status",
                accessorKey: "status",
                cell: ({ row }) => statusBadge(row.getValue("status")),
                maxSize: STATUS_HEADER_MAX_SIZE,
                enableSorting: false
            }
        ],
        [t]
    );
}

type AddressCellProps = {
    claimId: string;
};

const AddressCell = ({ claimId }: AddressCellProps) => {
    const { t } = useTranslation("claimsInvoicesOverview");

    return (
        <Flex gap="0.5rem" alignItems="center" justifyContent="center" w="100%">
            <Tooltip
                showArrow
                content={t("claimsTable.copyInvoiceAddress")}
                positioning={{
                    placement: "top"
                }}
            >
                <span>
                    <InvoiceAddressModal claimIdRef={claimId} />
                </span>
            </Tooltip>
        </Flex>
    );
};

type CreateInvoiceCellProps = {
    rowId: string;
    claimId: string;
    invoiceCount: ReactNode;
};

const CreateInvoiceCell = ({ rowId, claimId, invoiceCount }: CreateInvoiceCellProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation(["claimsInvoicesOverview", "errorMessages"]);
    const { mutate: createInvoice, isPending: isCreatingInvoice } = useMutation(createEmptyInvoiceMutationOptions);
    const { mutate: updateInvoice, isPending: isUpdatingInvoice } = useMutation(updateInvoiceByPutMutationOptions);
    const { mutate: deleteInvoice } = useMutation(deleteInvoiceMutationOptions);
    const isCreatingOrUpdatingInvoice = isCreatingInvoice || isUpdatingInvoice;

    useSlowRequestToast({
        isLoading: isCreatingOrUpdatingInvoice
    });

    const createInvoiceFailed = useCallback(() => {
        toaster.create({
            id: "createInvoiceFailed",
            title: t("errorMessages:createInvoiceFailed"),
            type: "error",
            duration: TOAST_DURATION,
            closable: true
        });
    }, [t]);

    const handleCreateInvoice = useCallback(() => {
        const actOnBehalfOf = globalThis.sessionStorage.getItem("actOnBehalfOf");
        const companyCrmId = actOnBehalfOf ? JSON.parse(actOnBehalfOf).crmId.toString() : "";

        createInvoice(
            { crmId: companyCrmId },
            {
                onSuccess: (invoiceId: string) => {
                    updateInvoice(
                        { id: invoiceId, invoice: { claimsRef: claimId } },
                        {
                            onSuccess: () => {
                                navigate(`/invoice/${invoiceId}`);
                            },
                            onError: () => {
                                createInvoiceFailed();
                                deleteInvoice(invoiceId);
                            }
                        }
                    );
                },
                onError: () => {
                    createInvoiceFailed();
                }
            }
        );
    }, [claimId, createInvoice, updateInvoice, navigate, createInvoiceFailed, deleteInvoice]);

    return (
        <Flex gap="0.5rem" alignItems="center" justifyContent="end">
            <Text>{invoiceCount}</Text>
            <Tooltip
                showArrow
                content={t("claimsTable.createNewInvoice")}
                positioning={{
                    placement: "top"
                }}
            >
                <span>
                    <CreateInvoiceButton
                        rowId={rowId}
                        onCreateInvoice={handleCreateInvoice}
                        isCreatingInvoice={isCreatingOrUpdatingInvoice}
                    />
                </span>
            </Tooltip>
        </Flex>
    );
};

type CreateInvoiceButtonProps = {
    rowId: string;
    onCreateInvoice: () => void;
    isCreatingInvoice: boolean;
};

const CreateInvoiceButton = ({ rowId, onCreateInvoice, isCreatingInvoice }: CreateInvoiceButtonProps) => {
    const { t } = useTranslation("claimsInvoicesOverview");

    const handleClicked = useCallback(
        (e: React.UIEvent) => {
            if (e.type === "keyup") {
                const keyEvent = e as React.KeyboardEvent;
                if (keyEvent.key === "Enter") {
                    e.stopPropagation();
                    datadogLogCreateInvoiceViaPlusButton();
                    onCreateInvoice();
                }
            }
            if (e.type === "click") {
                e.stopPropagation();
                datadogLogCreateInvoiceViaPlusButton();
                onCreateInvoice();
            }
        },
        [onCreateInvoice]
    );

    if (isCreatingInvoice) {
        return <Spinner />;
    }

    return (
        <Button
            id={"createInvoiceBtn" + rowId}
            size="sm"
            aria-label={t("claimsTable.createInvoice")}
            variant="icon-only"
            icon={<FiPlus />}
            onClick={handleClicked}
            onKeyUp={handleClicked}
            tabIndex={0}
        />
    );
};

const datadogLogCreateInvoiceViaPlusButton = () => {
    try {
        datadogRum.addAction("create_invoice_via_plus_button");
    } catch {
        // Never block invoice creation flows if telemetry fails.
    }
};
